import React, { useState } from 'react';
import { destinations } from '@/data/destinations';
import { MapPin, ArrowRight } from 'lucide-react';

interface MapRegion {
  id: string;
  name: string;
  x: number;
  y: number;
  destinations: typeof destinations;
}

const DestinationMap: React.FC = () => {
  const [activeRegion, setActiveRegion] = useState<string | null>(null);

  const regions: MapRegion[] = [
    {
      id: 'albanian-riviera',
      name: 'Albanian Riviera',
      x: 38,
      y: 72,
      destinations: destinations.filter((d) => d.region === 'Albanian Riviera'),
    },
    {
      id: 'central-albania',
      name: 'Central Albania',
      x: 48,
      y: 48,
      destinations: destinations.filter((d) => d.region === 'Central Albania'),
    },
    {
      id: 'southern-albania',
      name: 'Southern Albania',
      x: 42,
      y: 62,
      destinations: destinations.filter((d) => d.region === 'Southern Albania'),
    },
    {
      id: 'northern-albania',
      name: 'Northern Albania',
      x: 48,
      y: 28,
      destinations: destinations.filter((d) => d.region === 'Northern Albania'),
    },
  ];

  const activeRegionData = regions.find((r) => r.id === activeRegion);

  return (
    <section className="py-24 lg:py-32 bg-[#F5F1E8]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
            Explore the Regions
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6 gold-underline">
            Discover Albania
          </h2>
          <p className="font-sans text-sm text-[#777] max-w-lg mx-auto leading-relaxed mt-10">
            From the sun-kissed Riviera to the dramatic northern Alps, select a region to explore our curated Albanian destinations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Map Area */}
          <div className="lg:col-span-3 relative">
            <div className="relative bg-[#1A3B5C]/5 rounded-sm overflow-hidden aspect-[3/4] max-w-[500px] mx-auto">
              {/* Albania Map SVG */}
              <svg
                viewBox="0 0 500 650"
                className="w-full h-full"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Albania outline - simplified */}
                <path
                  d="M200 50 L260 40 L310 60 L340 100 L350 150 L360 200 L350 250 L340 280 L330 310 L310 340 L290 370 L270 400 L250 430 L230 460 L210 490 L190 510 L170 520 L160 500 L155 470 L160 440 L170 410 L165 380 L160 350 L155 320 L160 290 L170 260 L175 230 L170 200 L165 170 L170 140 L180 110 L190 80 Z"
                  fill="#1A3B5C" fillOpacity="0.06" stroke="#1A3B5C" strokeOpacity="0.2" strokeWidth="2"
                />

                {/* Adriatic Sea label */}
                <text x="80" y="300" fill="#1A3B5C" fillOpacity="0.15" fontSize="14" fontFamily="sans-serif" fontWeight="300" letterSpacing="3" transform="rotate(-75, 80, 300)">
                  ADRIATIC SEA
                </text>

                {/* Ionian Sea label */}
                <text x="100" y="520" fill="#1A3B5C" fillOpacity="0.15" fontSize="12" fontFamily="sans-serif" fontWeight="300" letterSpacing="3">
                  IONIAN SEA
                </text>

                {/* Grid lines */}
                {[0, 100, 200, 300, 400, 500, 600].map((y) => (
                  <line key={`h${y}`} x1="0" y1={y} x2="500" y2={y} stroke="#1A3B5C" strokeOpacity="0.04" strokeWidth="0.5" />
                ))}
                {[0, 100, 200, 300, 400].map((x) => (
                  <line key={`v${x}`} x1={x} y1="0" x2={x} y2="650" stroke="#1A3B5C" strokeOpacity="0.04" strokeWidth="0.5" />
                ))}

                {/* Key cities */}
                <text x="260" y="75" fill="#1A3B5C" fillOpacity="0.2" fontSize="9" fontFamily="sans-serif" textAnchor="middle">Shkodër</text>
                <text x="280" y="195" fill="#1A3B5C" fillOpacity="0.2" fontSize="10" fontFamily="sans-serif" textAnchor="middle">TIRANA</text>
                <text x="310" y="240" fill="#1A3B5C" fillOpacity="0.2" fontSize="9" fontFamily="sans-serif" textAnchor="middle">Elbasan</text>
                <text x="250" y="340" fill="#1A3B5C" fillOpacity="0.2" fontSize="9" fontFamily="sans-serif" textAnchor="middle">Berat</text>
                <text x="230" y="440" fill="#1A3B5C" fillOpacity="0.2" fontSize="9" fontFamily="sans-serif" textAnchor="middle">Gjirokastër</text>
                <text x="190" y="500" fill="#1A3B5C" fillOpacity="0.2" fontSize="9" fontFamily="sans-serif" textAnchor="middle">Sarandë</text>

                {/* Region markers */}
                {regions.map((region) => {
                  const cx = region.x * 5;
                  const cy = region.y * 8;
                  return (
                    <g key={region.id}>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={activeRegion === region.id ? 20 : 14}
                        fill={activeRegion === region.id ? '#C9A961' : '#1A3B5C'}
                        fillOpacity={activeRegion === region.id ? 0.9 : 0.6}
                        className="cursor-pointer transition-all duration-300"
                        onClick={() => setActiveRegion(activeRegion === region.id ? null : region.id)}
                      />
                      {/* Pulse animation for active */}
                      {activeRegion === region.id && (
                        <circle
                          cx={cx}
                          cy={cy}
                          r="26"
                          fill="none"
                          stroke="#C9A961"
                          strokeWidth="1"
                          opacity="0.4"
                        >
                          <animate attributeName="r" from="20" to="34" dur="1.5s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="0.4" to="0" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                      )}
                      {/* Inner dot */}
                      <circle
                        cx={cx}
                        cy={cy}
                        r="4"
                        fill="white"
                        className="cursor-pointer"
                        onClick={() => setActiveRegion(activeRegion === region.id ? null : region.id)}
                      />
                      {/* Label */}
                      <text
                        x={cx}
                        y={cy + 28}
                        textAnchor="middle"
                        className="cursor-pointer"
                        fill={activeRegion === region.id ? '#C9A961' : '#1A3B5C'}
                        fontSize="10"
                        fontFamily="Montserrat"
                        fontWeight="500"
                        letterSpacing="1"
                        onClick={() => setActiveRegion(activeRegion === region.id ? null : region.id)}
                      >
                        {region.name.toUpperCase()}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Region Details Panel */}
          <div className="lg:col-span-2">
            {activeRegionData ? (
              <div className="animate-fade-in">
                <h3 className="font-serif text-3xl text-[#1A3B5C] mb-2">
                  {activeRegionData.name}
                </h3>
                <p className="font-sans text-xs tracking-wider text-[#C9A961] uppercase mb-8">
                  {activeRegionData.destinations.length} {activeRegionData.destinations.length === 1 ? 'Destination' : 'Destinations'}
                </p>

                <div className="space-y-6">
                  {activeRegionData.destinations.map((dest) => (
                    <div
                      key={dest.id}
                      className="flex gap-4 group cursor-pointer"
                      onClick={() => {
                        const el = document.getElementById('destinations');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <div className="w-24 h-20 flex-shrink-0 img-zoom">
                        <img
                          src={dest.image}
                          alt={dest.alt || `${dest.name} in ${dest.region}, Albania`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="font-serif text-lg text-[#1A3B5C] group-hover:text-[#C9A961] transition-colors truncate">
                          {dest.name}
                        </h4>
                        <div className="flex items-center gap-1 mb-1">
                          <MapPin className="w-3 h-3 text-[#C9A961]" />
                          <span className="font-sans text-[10px] text-[#999] truncate">
                            {dest.location}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const el = document.getElementById('plan-journey');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="font-sans text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors mt-1"
                        >
                          Book Now
                        </button>

                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <MapPin className="w-10 h-10 text-[#C9A961]/40 mb-4" />
                <h3 className="font-serif text-2xl text-[#1A3B5C] mb-3">
                  Explore Albania's Regions
                </h3>
                <p className="font-sans text-xs text-[#999] leading-relaxed max-w-xs">
                  Click on any marker on the map to explore our curated destinations across Albania — from the Riviera to the Alps.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DestinationMap;
